'use client';

import { useState, type ReactNode } from 'react';
import dynamic from 'next/dynamic';
import Icon from '@pixum/icon';
import Text, { TextVariant } from '@pixum/text';
import type { ArticleCreationMetadata, Device } from 'data-layer';
import type { ArticlePreviewDialogProps } from './ArticlePreviewDialog/ArticlePreviewDialog';
import styles from '../ArticlePreviewControl.module.css';

const ArticlePreviewDialog = dynamic<ArticlePreviewDialogProps>(
  () => import('./ArticlePreviewDialog/ArticlePreviewDialog').then(m => ({ default: m.ArticlePreviewDialog })),
  { ssr: false }
);

export interface ArticlePreviewActionProps {
  metadata: ArticleCreationMetadata;
  previewLabel: string;
  editUrl?: string | null;
  productShortTitle?: string;
  articleGroup?: string;
  constants?: Record<string, string>;
  device?: Device;
  children?: ReactNode;
}

export function ArticlePreviewAction({
  metadata,
  previewLabel,
  editUrl,
  productShortTitle = '',
  articleGroup = '',
  constants = {},
  device = 'desktop',
  children
}: ArticlePreviewActionProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className={styles['action']}
        aria-label={previewLabel}
        onClick={() => setIsDialogOpen(true)}
      >
        {children}
        <span className={styles['actionLabel']}>
          <Icon aria-hidden="true" className={styles['icon']} name="zoom-in" />
          <Text className={styles['text']} variant={TextVariant.SUBHEADLINE}>
            {previewLabel}
          </Text>
        </span>
      </button>
      {isDialogOpen && (
        <ArticlePreviewDialog
          metadataPromise={Promise.resolve(metadata)}
          editUrl={editUrl ?? null}
          productShortTitle={productShortTitle}
          articleGroup={articleGroup}
          constants={constants}
          device={device}
          onClose={() => setIsDialogOpen(false)}
        />
      )}
    </>
  );
}
